import { makeStyles } from "tss-react/mui"
import { Box, Divider, Grid, Typography } from "@mui/material"
import OrderStatusBadge from "../../../../../../shared/components/badges/OrderStatusBadge"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { ProductionSlip } from "../ordersForCalendarTypes"
import { StandardQRCode } from "../../../../../../shared/components/qrCode/StandardQRCode"

const useStyles = makeStyles()((theme) => ({
    title: {
        color: theme.palette.tailwind.slate[700],
    },
    listBox: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
    },
    itemWrap: {
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "nowrap",
        gap: "1rem",
    },
    textColor: {
        color: theme.palette.tailwind.slate[600],
    },
    accentColor: {
        color: theme.palette.tailwind.slate[900],
    },
    textRight: {
        textAlign: "right",
    },
    textCenter: {
        textAlign: "center",
    },
    boxSpacing: {
        gap: "1rem",
    },
}))

type ProductionSlipDialogDetailsProps = {
    order: ProductionSlip
}

export const ProductionSlipDialogDetails = ({ order }: ProductionSlipDialogDetailsProps) => {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const { id, orderStatus, meetingDetails, deliveryDetails, customer, organizers, webshop, permaLink } = order

    return (
        <Grid item xs={5} className={classes.listBox}>
            <Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-order-id" vars={{ orderId: id.toString() }}>
                        <Typography variant="h6" className={classes.title}>
                            Order ID: {id}
                        </Typography>
                    </LocalizedStrict>
                    <OrderStatusBadge status={orderStatus} />
                </Box>
                <Typography variant="body1" className={classes.textColor}>
                    {meetingDetails.meetingName}
                </Typography>
            </Box>
            <Divider />
            <Grid container flexDirection="column" className={classes.boxSpacing}>
                <LocalizedStrict id="production-slip-dialog-delivery-details">
                    <Typography variant="h6" className={classes.title}>
                        Delivery details
                    </Typography>
                </LocalizedStrict>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-delivery-type">
                        <Typography variant="body1" className={classes.textColor}>
                            Delivery type:
                        </Typography>
                    </LocalizedStrict>
                    <Typography
                        variant="subtitle1"
                        className={cx(classes.accentColor, classes.textRight)}
                        textTransform="capitalize"
                    >
                        {deliveryDetails.type.toLowerCase()}
                    </Typography>
                </Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-delivery-time">
                        <Typography variant="body1" className={classes.textColor}>
                            Delivery time:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                        {deliveryDetails?.time ? dateTimeFactory.fromApi(deliveryDetails.time).toFormat("HH:mm") : "-"}
                    </Typography>
                </Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-delivery-meeting-time">
                        <Typography variant="body1" className={classes.textColor}>
                            Meeting time:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                        {meetingDetails?.startDateTime && meetingDetails?.endDateTime
                            ? dateTimeFactory.formatPeriod(
                                  dateTimeFactory.fromApi(meetingDetails.startDateTime),
                                  dateTimeFactory.fromApi(meetingDetails.endDateTime),
                              )
                            : "-"}
                    </Typography>
                </Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-delivery-delivery-location">
                        <Typography variant="body1" className={classes.textColor}>
                            Delivery location:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                        {deliveryDetails?.location?.name ?? "-"}
                    </Typography>
                </Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-delivery-sittings">
                        <Typography variant="body1" className={classes.textColor}>
                            Sittings:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                        {deliveryDetails?.sittings ?? "-"}
                    </Typography>
                </Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-delivery-shop">
                        <Typography variant="body1" className={classes.textColor}>
                            Shop:
                        </Typography>
                    </LocalizedStrict>
                    <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                        {webshop?.name ?? "-"}
                    </Typography>
                </Box>
            </Grid>
            <Divider />
            <Grid container flexDirection="column" className={classes.boxSpacing}>
                <LocalizedStrict id="production-slip-dialog-contact-details">
                    <Typography variant="h6" className={classes.title}>
                        Contact details
                    </Typography>
                </LocalizedStrict>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-contact-details-creator">
                        <Typography variant="body1" className={classes.textColor}>
                            Creator:
                        </Typography>
                    </LocalizedStrict>
                    <Box>
                        <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                            {customer.displayName}
                        </Typography>
                        {customer?.email && (
                            <Typography variant="body1" className={cx(classes.accentColor, classes.textRight)}>
                                {customer.email}
                            </Typography>
                        )}
                        {customer?.mobileNumber && (
                            <Typography variant="body1" className={cx(classes.accentColor, classes.textRight)}>
                                {customer.mobileNumber}
                            </Typography>
                        )}
                        {customer?.phoneNumber && (
                            <Typography variant="body1" className={cx(classes.accentColor, classes.textRight)}>
                                {customer.phoneNumber}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box className={classes.itemWrap}>
                    <LocalizedStrict id="production-slip-dialog-contact-details-meeting-host">
                        <Typography variant="body1" className={classes.textColor}>
                            Meeting host:
                        </Typography>
                    </LocalizedStrict>
                    <Grid container flexDirection="column" className={classes.boxSpacing}>
                        {organizers.map((organizer, index) => (
                            <Box key={index}>
                                <Typography variant="subtitle1" className={cx(classes.accentColor, classes.textRight)}>
                                    {organizer.name}
                                </Typography>
                                {organizer?.email && (
                                    <Typography variant="body1" className={cx(classes.accentColor, classes.textRight)}>
                                        {organizer.email}
                                    </Typography>
                                )}
                                {organizer?.phone && (
                                    <Typography variant="body1" className={cx(classes.accentColor, classes.textRight)}>
                                        {organizer.phone}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Grid>
                </Box>
            </Grid>
            {permaLink && (
                <>
                    <Divider />
                    <Grid container flexDirection="column" alignItems="center" className={classes.boxSpacing}>
                        <LocalizedStrict id="production-slip-dialog-scan">
                            <Typography variant="body1" className={cx(classes.textColor, classes.textCenter)}>
                                View production slip on mobile device
                            </Typography>
                        </LocalizedStrict>
                        <StandardQRCode url={permaLink} />
                    </Grid>
                </>
            )}
        </Grid>
    )
}
